/**
 * Image Gallery V2
 *
 * Grid-Layout für Bilder mit:
 * - Mehrfachauswahl mit Checkbox
 * - Bulk-Aktionen (Download, Löschen)
 * - Generating Placeholder während Generierung
 * - Featured Image nach erfolgreicher Generierung
 * - Pagination
 */

import { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageCard } from './ImageCard';
import { ImageDetailModal } from './ImageDetailModal';
import { GeneratingPlaceholder } from './GeneratingPlaceholder';
import { SelectionActionBar } from './SelectionActionBar';
import { GalleryAddToProductBridge } from './GalleryAddToProductBridge';
import { Loading, EmptyState } from '@/components/v2/ui';
import { useImageActions } from '@/hooks/useImageActions';
import { useUnifiedAddToProductFlag } from '@/hooks/useUnifiedAddToProductFlag';
import { cn } from '@/lib/utils';
import type { Image, ImagePagination } from '@/types';

export interface ImageGalleryProps {
  images: Image[];
  isLoading: boolean;
  isFetching: boolean;
  pagination?: ImagePagination;
  onPageChange: (page: number) => void;
  /** Zeigt den Generierungs-Platzhalter an */
  isGenerating?: boolean;
  /** Startzeitpunkt der Generierung */
  generationStartTime?: Date;
  /** Zeigt das erste Bild als Featured (nur nach Generierung) */
  showFeatured?: boolean;
  /** Set der ausgewählten Bild-IDs (von Parent verwaltet) */
  selectedIds?: Set<string>;
  /** Callback wenn Auswahl geändert wird */
  onSelectionChange?: (ids: Set<string>) => void;
}

export function ImageGallery({
  images,
  isLoading,
  isFetching,
  pagination,
  onPageChange,
  isGenerating = false,
  generationStartTime,
  showFeatured = false,
  selectedIds: externalSelectedIds,
  onSelectionChange,
}: ImageGalleryProps) {
  const { t } = useTranslation();

  // Derived State Pattern: Store ID only, derive Image from images array
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Derive selectedImage from images array (always fresh from React Query cache!)
  const selectedImage = useMemo(
    () => images.find((img) => img.id === selectedImageId) || null,
    [images, selectedImageId]
  );

  // Mehrfachauswahl State - extern oder intern verwaltet
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Add-to-Product (Multi-Select): nur wenn Feature-Flag aktiv ist —
  // Legacy hat keinen Multi-Pfad, daher bei Flag OFF schlicht abwesend.
  const addToProductEnabled = useUnifiedAddToProductFlag();
  const [addToProductOpen, setAddToProductOpen] = useState(false);

  const { downloadImage, deleteImage } = useImageActions();

  const handleViewDetails = (image: Image) => {
    setSelectedImageId(image.id); // Store ID only
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImageId(null); // Reset ID
  };

  // Navigation
  const idx = selectedImageId ? images.findIndex((img) => img.id === selectedImageId) : -1;
  const handleNavigatePrev = useCallback(() => {
    if (!selectedImageId) return;
    const i = images.findIndex((img) => img.id === selectedImageId);
    if (i > 0) setSelectedImageId(images[i - 1].id);
  }, [selectedImageId, images]);
  const handleNavigateNext = useCallback(() => {
    if (!selectedImageId) return;
    const i = images.findIndex((img) => img.id === selectedImageId);
    if (i >= 0 && i < images.length - 1) setSelectedImageId(images[i + 1].id);
  }, [selectedImageId, images]);

  // Auswahl-Handlers
  const handleSelectionChange = useCallback(
    (imageId: string, selected: boolean) => {
      const newSet = new Set(selectedIds);
      if (selected) {
        newSet.add(imageId);
      } else {
        newSet.delete(imageId);
      }
      setSelectedIds(newSet);
    },
    [selectedIds, setSelectedIds]
  );

  const handleSelectAll = useCallback(() => {
    const allIds = new Set(images.map((img) => img.id));
    setSelectedIds(allIds);
  }, [images, setSelectedIds]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, [setSelectedIds]);

  // Bulk Download
  const handleBulkDownload = useCallback(async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDownloading(true);
    try {
      // Download alle ausgewählten Bilder
      for (const imageId of selectedIds) {
        await downloadImage(imageId);
      }
      toast.success(t('toast.success.imagesDownloaded', { count: selectedIds.size }));
    } catch {
      toast.error(t('toast.error.downloading'));
    } finally {
      setIsBulkDownloading(false);
    }
  }, [selectedIds, downloadImage, t]);

  // Bulk Delete
  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const confirmMessage =
      selectedIds.size === 1
        ? t('gallery.deleteConfirmSingle')
        : t('gallery.deleteConfirmMultiple', { count: selectedIds.size });

    if (!window.confirm(confirmMessage)) return;

    setIsBulkDeleting(true);
    try {
      // Lösche alle ausgewählten Bilder
      for (const imageId of selectedIds) {
        await deleteImage(imageId);
      }
      toast.success(t('toast.success.imagesDeleted', { count: selectedIds.size }));
      setSelectedIds(new Set());
    } catch {
      toast.error(t('toast.error.deleting'));
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, deleteImage, setSelectedIds, t]);

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loading size="lg" />
      </div>
    );
  }

  // Empty State (aber nicht wenn gerade generiert wird)
  if (!isGenerating && (!images || images.length === 0)) {
    return (
      <EmptyState
        icon={<ImageIcon className="h-16 w-16" />}
        title={t('gallery.noImages')}
        description={t('generation.formTitle')}
      />
    );
  }

  // Featured Image nur anzeigen wenn showFeatured=true
  const shouldShowFeatured = showFeatured && pagination?.page === 1 && images.length > 0;
  const featuredImage = shouldShowFeatured ? images[0] : null;
  const remainingImages = shouldShowFeatured ? images.slice(1) : images;

  return (
    <div className="relative">
      {/* Fetching Indicator */}
      {isFetching && !isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
        </div>
      )}

      {/* Generating Placeholder - zentriert in eigener Reihe */}
      {isGenerating && (
        <div className="flex justify-center mb-6">
          <div className="w-full max-w-md">
            <GeneratingPlaceholder startTime={generationStartTime} />
          </div>
        </div>
      )}

      {/* Featured Image - nur nach erfolgreicher Generierung */}
      {!isGenerating && featuredImage && (
        <div className="flex justify-center mb-6">
          <div className="w-full max-w-md">
            <ImageCard
              image={featuredImage}
              isFeatured
              onViewDetails={handleViewDetails}
              isSelected={selectedIds.has(featuredImage.id)}
              onSelectionChange={handleSelectionChange}
            />
          </div>
        </div>
      )}

      {/* Regular Image Grid */}
      {remainingImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {remainingImages.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              onViewDetails={handleViewDetails}
              isSelected={selectedIds.has(image.id)}
              onSelectionChange={handleSelectionChange}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <nav className="mt-8" aria-label="Galerie-Navigation">
          <div className="flex items-center justify-center gap-2">
            {/* Previous */}
            <button
              className={cn(
                'p-2 rounded-lg transition-colors',
                pagination.page <= 1
                  ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                  : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Page Numbers */}
            {generatePageNumbers(pagination.page, pagination.total_pages).map((pageNum, idx) => (
              <button
                key={idx}
                className={cn(
                  'min-w-[40px] h-10 rounded-lg text-sm font-medium transition-colors',
                  pageNum === pagination.page
                    ? 'bg-orange-500 text-white'
                    : pageNum === '...'
                      ? 'text-neutral-400 cursor-default'
                      : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
                onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
                disabled={pageNum === '...'}
              >
                {pageNum}
              </button>
            ))}

            {/* Next */}
            <button
              className={cn(
                'p-2 rounded-lg transition-colors',
                pagination.page >= pagination.total_pages
                  ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                  : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </nav>
      )}

      {/* Image Detail Modal */}
      <ImageDetailModal
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onNavigatePrev={idx > 0 ? handleNavigatePrev : undefined}
        onNavigateNext={idx >= 0 && idx < images.length - 1 ? handleNavigateNext : undefined}
      />

      {/* Selection Action Bar (unten mittig) */}
      <SelectionActionBar
        selectedCount={selectedIds.size}
        totalCount={images.length}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        onDownload={handleBulkDownload}
        onDelete={handleBulkDelete}
        isDownloading={isBulkDownloading}
        isDeleting={isBulkDeleting}
        onAddToProduct={
          addToProductEnabled ? () => setAddToProductOpen(true) : undefined
        }
      />

      {/* Add-to-Product Overlay (Multi-Select, nur Flag ON) */}
      <GalleryAddToProductBridge
        open={addToProductOpen}
        imageIds={[...selectedIds]}
        onClose={() => setAddToProductOpen(false)}
        onComplete={() => {
          handleDeselectAll();
          setAddToProductOpen(false);
        }}
      />
    </div>
  );
}

/**
 * Generiert Seitenzahlen für Pagination
 */
function generatePageNumbers(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  const delta = 2;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    if (current > delta + 2) {
      pages.push('...');
    }
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      pages.push(i);
    }
    if (current < total - delta - 1) {
      pages.push('...');
    }
    if (total > 1) {
      pages.push(total);
    }
  }

  return pages;
}

export default ImageGallery;
